import type {
  AvailableTourTimes,
  InstantTourProviders,
  ScheduledTour,
  TourProvider,
  TourType,
} from '@/src/lib/api/endpoints/hpTours';
import {
  appendTimeISOSubstringIfMissing,
  isDateInThePast,
  parseISODateString,
} from '@/src/components/features/property/lib/dateUtils';

export const TOUR_TYPES = {
  IN_PERSON: 'IN_PERSON',
  SELF_GUIDED: 'SELF_GUIDED',
  LIVE_VIDEO: 'LIVE_VIDEO',
} as const satisfies Record<string, TourType>;

export const TOUR_TYPE_LABELS: Record<TourType, string> = {
  IN_PERSON: 'In-person tour',
  SELF_GUIDED: 'Self-guided tour',
  LIVE_VIDEO: 'Live video tour',
};

export const TOUR_TYPE_DESCRIPTIONS: Record<TourType, string> = {
  IN_PERSON: 'Tour on site with the property manager guiding you.',
  SELF_GUIDED: 'Tour on site at your own pace.',
  LIVE_VIDEO: 'Video chat from home with the property manager guiding you.',
};

export type TourOption = {
  type: TourType;
  provider: TourProvider;
};

export type AvailableToursMap = Record<string, Array<string>>;

const addScheduledDateTimeToAvailableTours = (
  availableDatesTimes: Array<string>,
  scheduledDateWithOrWithoutTime: string,
) => {
  const scheduledDateTime = appendTimeISOSubstringIfMissing(scheduledDateWithOrWithoutTime);
  const doNotAddScheduledTour =
    availableDatesTimes.includes(scheduledDateWithOrWithoutTime) || isDateInThePast(scheduledDateTime);

  if (doNotAddScheduledTour) return;

  const dateToAdd = new Date(scheduledDateWithOrWithoutTime);
  const scheduledTourIndex = availableDatesTimes.findIndex(
    (availableDateTime) => dateToAdd < new Date(availableDateTime),
  );
  if (scheduledTourIndex !== -1) {
    availableDatesTimes.splice(scheduledTourIndex, 0, scheduledDateWithOrWithoutTime);
  } else {
    availableDatesTimes.push(scheduledDateWithOrWithoutTime);
  }
};

export function getTourOptions(providers?: InstantTourProviders | null): Array<TourOption> {
  if (!providers) return [];

  return [
    providers.inPersonProvider ? { type: TOUR_TYPES.IN_PERSON, provider: providers.inPersonProvider } : null,
    providers.selfGuidedProvider ? { type: TOUR_TYPES.SELF_GUIDED, provider: providers.selfGuidedProvider } : null,
    providers.liveVideoProvider ? { type: TOUR_TYPES.LIVE_VIDEO, provider: providers.liveVideoProvider } : null,
  ].filter((option): option is TourOption => option !== null);
}

export function getTourProvider(
  providers: InstantTourProviders | null | undefined,
  tourType: TourType | '',
): TourProvider | null {
  if (!providers || !tourType) return null;

  if (tourType === TOUR_TYPES.IN_PERSON) return providers.inPersonProvider ?? null;
  if (tourType === TOUR_TYPES.SELF_GUIDED) return providers.selfGuidedProvider ?? null;
  return providers.liveVideoProvider ?? null;
}

export function getScheduledTour(
  listingAlias: string | undefined,
  scheduledTours?: Array<ScheduledTour>,
): ScheduledTour | null {
  if (!listingAlias) return null;
  return scheduledTours?.find((tour) => tour.listingAlias === listingAlias) ?? null;
}

export function buildAvailableToursMap(
  availableTourTimes: AvailableTourTimes | null | undefined,
  scheduledDateWithOrWithoutTime = '',
): AvailableToursMap {
  const availableToursMap: AvailableToursMap = {};
  const availableTimes = availableTourTimes?.availableTimes ?? [];
  const zoneId = availableTourTimes?.zoneId ?? '';
  const dateOnly = availableTourTimes?.dateOnly ?? false;

  availableTimes.forEach((availableDateWithOrWithoutTime) => {
    const availableDateTime = appendTimeISOSubstringIfMissing(availableDateWithOrWithoutTime);
    const dateAsKey = parseISODateString(availableDateTime, zoneId, 'yyyy-MM-dd') as string;

    if (!(dateAsKey in availableToursMap)) {
      availableToursMap[dateAsKey] = [];
    }

    availableToursMap[dateAsKey].push(availableDateTime);
  });

  if (!scheduledDateWithOrWithoutTime || !scheduledDateWithOrWithoutTime.includes('T') || dateOnly) {
    return availableToursMap;
  }

  const scheduledDateAsKey = parseISODateString(scheduledDateWithOrWithoutTime, zoneId, 'yyyy-MM-dd') as string;
  if (!(scheduledDateAsKey in availableToursMap)) {
    return availableToursMap;
  }

  addScheduledDateTimeToAvailableTours(availableToursMap[scheduledDateAsKey], scheduledDateWithOrWithoutTime);

  return availableToursMap;
}

export function formatTourDateTime(dateTime: string, zoneId = '', dateOnly = false): string {
  if (!dateTime) return '';
  const normalized = appendTimeISOSubstringIfMissing(dateTime);
  const pattern = dateOnly ? 'EEEE, MMM dd' : "EEEE, MMM dd 'at' h:mm a";
  return parseISODateString(normalized, zoneId || 'UTC', pattern) as string;
}

export function splitFullName(name: string): { firstName: string; lastName: string } {
  const [firstName = '', ...lastNameTokens] = name.trim().split(/\s+/);
  return { firstName, lastName: lastNameTokens.join(' ') };
}

export function hasFullName(name: string): boolean {
  const { firstName, lastName } = splitFullName(name);
  return Boolean(firstName && lastName);
}

export function isValidPhoneNumber(phone: string): boolean {
  return phone.replace(/\D/g, '').length === 10;
}

export function formatPhoneNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
